import {createApp} from 'vue';
import {GET} from '../modules/fetch.ts';
import {fomanticQuery} from '../modules/fomantic/base.ts';
import {createElementFromHTML, activePageTimerRefresh, hideElem, toggleElem} from '../utils/dom.ts';
import {registerGlobalEventFunc} from '../modules/observer.ts';
import type {JQueryElem} from '../types.ts';
import {initRepoPullCommitStatusFilter} from './repo-pull-checks-filter.ts';
import {svg} from '../svg.ts';
import {syncPullRequestFavicon} from '../modules/favicon-status.ts';

export function initRepoPullRequestUpdate(el: HTMLElement) {
  const elDropdown = el.querySelector(':scope > .ui.dropdown');
  if (!elDropdown) return;
  const elButton = el.querySelector<HTMLButtonElement>(':scope > button')!;

  fomanticQuery(elDropdown).dropdown({
    onChange(_text: string, _value: string, $choice: JQueryElem) {
      const choiceEl = $choice[0];
      elButton.textContent = choiceEl.textContent;
      elButton.setAttribute('data-url', choiceEl.getAttribute('data-update-url')!);
    },
  });
}

export function onCommitStatusChecksToggle(btn: HTMLElement) {
  const panel = btn.closest('.commit-status-toggle')!.parentElement!;
  const list = panel.querySelector<HTMLElement>('.commit-status-list')!;
  list.style.maxHeight = list.style.maxHeight ? '' : '0px'; // toggle
  const expanded = !list.style.maxHeight;
  const label = btn.getAttribute(expanded ? 'data-hide-all' : 'data-show-all')!;
  btn.setAttribute('aria-expanded', String(expanded));
  btn.setAttribute('aria-label', label);
  btn.setAttribute('data-tooltip-content', label);
  btn.innerHTML = svg(`octicon-chevron-${expanded ? 'down' : 'right'}`, 18);
}

export function onPullMergeInstructionsShow() {
  document.querySelector<HTMLDetailsElement>('#pull-merge-instructions')!.open = true;
}

async function initRepoPullRequestMergeForm(box: HTMLElement) {
  const el = box.querySelector('#pull-request-merge-form');
  if (!el) return;

  const data = JSON.parse(el.getAttribute('data-merge-form-props')!);
  const {default: PullRequestMergeForm} = await import('../components/PullRequestMergeForm.vue');
  const view = createApp(PullRequestMergeForm, {mergeFormProps: data});
  view.mount(el); // TODO: can unmount when reloaded?
}

function initRepoPullMergeBoxRefresh(el: Element) {
  // The merge box has complex buttons & form, if the user has interacted with any element, don't refresh.
  // Otherwise, the user won't be able to merge or schedule a merge (auto-merge) when the PR status is not ready.
  let interacted = false;
  const interactionEvents = ['focusin', 'mousedown', 'click', 'keydown', 'input'];
  for (const event of interactionEvents) {
    el.addEventListener(event, (e) => {
      // The check visibility filter only changes the view, so using it does not stop the refresh.
      if ((e.target as Element).closest?.('.commit-status-filter, .commit-status-completed-toggle')) return;
      interacted = true;
      el.setAttribute('data-user-interacted', '');
    }, {capture: true});
  }

  activePageTimerRefresh({
    once: true, // on successful refresh, the data-global-init will re-initialize the element
    interval: () => interacted ? 0 : Number(el.getAttribute('data-pull-merge-box-reloading-interval')),
    async callback() {
      if (interacted) return;
      const pullLink = el.getAttribute('data-pull-link')!;
      const resp = await GET(`${pullLink}/merge_box`);
      if (!resp.ok) return;
      const respText = (await resp.text()).trim();
      if (!respText) {
        hideElem('#pull-merge-conflict-link');
        el.remove(); // merge box might not exist if the PR has changed (e.g.: merged and the head branch has been deleted)
        syncPullRequestFavicon(document);
        return;
      }
      const newEl = createElementFromHTML(respText);
      const scrollTop = el.querySelector<HTMLElement>('.commit-status-list')?.scrollTop;
      el.replaceWith(newEl); // don't morph, do full replacement to make sure data-global-init and Vue components are re-initialized
      if (scrollTop) newEl.querySelector<HTMLElement>('.commit-status-list')?.scrollTo({top: scrollTop, behavior: 'instant'});
    },
  });
}

export function initRepoPullMergeBox(el: HTMLElement) {
  syncPullRequestFavicon(document);
  registerGlobalEventFunc('click', 'onCommitStatusChecksToggle', onCommitStatusChecksToggle);
  registerGlobalEventFunc('click', 'onPullMergeInstructionsShow', onPullMergeInstructionsShow);
  toggleElem('#pull-merge-conflict-link', Boolean(el.querySelector('#pull-merge-conflicts')));
  initRepoPullRequestMergeForm(el);
  initRepoPullCommitStatusFilter(el);
  initRepoPullMergeBoxRefresh(el);
}
